import { useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Copy, Rss } from "lucide-react";
import api, { API } from "@/api";
import { toast } from "sonner";

const SEV = {
  critical: { icon: XCircle, cls: "text-[#8B1E21]", bg: "bg-[#8B1E21]/10" },
  warning: { icon: AlertTriangle, cls: "text-[#B8860B]", bg: "bg-[#D4AF37]/15" },
};

export default function AdminSeoHealth() {
  const [tab, setTab] = useState("launch");
  const [health, setHealth] = useState(null);
  const [launch, setLaunch] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setHealth(null); setLaunch(null);
    api.get("/admin/launch-check").then(({ data }) => setLaunch(data)).catch((e) => toast.error(e.friendlyMessage));
    api.get("/admin/seo-health").then(({ data }) => setHealth(data)).catch((e) => toast.error(e.friendlyMessage));
  };
  useEffect(load, []);

  const copy = (t, label) => { navigator.clipboard.writeText(t); toast.success(`${label} copied`); };
  const issues = health ? health.issues.filter((i) => filter === "all" || i.severity === filter) : [];

  return (
    <div className="max-w-4xl" data-testid="admin-seo-health">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl">SEO Health & Launch Readiness</h1>
          <p className="text-sm text-[#6E6D6A] mt-1">Automated audit of metadata, links, redirects, feeds and launch blockers.</p>
        </div>
        <button onClick={load} data-testid="seo-health-refresh" className="tq-btn-dark !py-2.5"><RefreshCw size={14} /> Re-run Audit</button>
      </div>

      <div className="flex gap-2 mb-6">
        {[["launch", "Launch Checklist"], ["issues", "SEO Issues"], ["feed", "Google Merchant Feed"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`seo-tab-${k}`}
            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors ${tab === k ? "bg-[#121212] text-[#D4AF37]" : "bg-white text-[#6E6D6A] hover:text-[#121212]"}`}>{l}</button>
        ))}
      </div>

      {tab === "launch" && (
        !launch ? <div className="h-48 bg-white animate-pulse" /> : (
          <div>
            <div className={`p-5 mb-5 flex items-center gap-4 ${launch.ready ? "bg-green-50 border border-green-200" : "bg-[#8B1E21]/5 border border-[#8B1E21]/30"}`} data-testid="launch-ready-banner">
              {launch.ready ? <CheckCircle2 size={28} className="text-green-600" /> : <XCircle size={28} className="text-[#8B1E21]" />}
              <div>
                <p className="font-semibold">{launch.ready ? "Store is launch-ready" : `${launch.summary.critical_failures} critical blocker(s) before launch`}</p>
                <p className="text-sm text-[#6E6D6A]">{launch.summary.passed} passed · {launch.summary.warnings} warning(s) · {launch.summary.critical_failures} critical</p>
              </div>
            </div>
            <div className="bg-white border border-[#E5DEC9] divide-y divide-[#F4EFE6]">
              {launch.checks.map((c) => (
                <div key={c.name} className="p-3.5 flex items-center gap-3 text-sm" data-testid={`launch-check-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {c.ok ? <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                    : c.critical ? <XCircle size={16} className="text-[#8B1E21] shrink-0" /> : <AlertTriangle size={16} className="text-[#B8860B] shrink-0" />}
                  <span className="font-medium w-52 shrink-0">{c.name}{c.critical && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-[#8B1E21]">critical</span>}</span>
                  <span className="text-[#6E6D6A] text-xs">{c.detail}</span>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {tab === "issues" && (
        !health ? <div className="h-48 bg-white animate-pulse" /> : (
          <div>
            <div className="flex items-center gap-3 mb-4 text-sm">
              <button onClick={() => setFilter("all")} data-testid="seo-filter-all" className={`px-3 py-1.5 ${filter === "all" ? "bg-[#121212] text-white" : "bg-white"}`}>All ({health.issues.length})</button>
              <button onClick={() => setFilter("critical")} data-testid="seo-filter-critical" className={`px-3 py-1.5 ${filter === "critical" ? "bg-[#8B1E21] text-white" : "bg-white text-[#8B1E21]"}`}>Critical ({health.counts.critical})</button>
              <button onClick={() => setFilter("warning")} data-testid="seo-filter-warning" className={`px-3 py-1.5 ${filter === "warning" ? "bg-[#D4AF37] text-[#121212]" : "bg-white text-[#B8860B]"}`}>Warnings ({health.counts.warning})</button>
              <span className="ml-auto text-xs text-[#9a938a]">Scanned: {Object.entries(health.scanned).map(([k, v]) => `${v} ${k}`).join(" · ")}</span>
            </div>
            {issues.length === 0 ? (
              <div className="bg-white border border-[#E5DEC9] p-10 text-center" data-testid="seo-no-issues">
                <CheckCircle2 size={32} className="mx-auto text-green-600 mb-2" />
                <p className="font-medium">No {filter === "all" ? "" : filter} issues found.</p>
              </div>
            ) : (
              <div className="bg-white border border-[#E5DEC9] divide-y divide-[#F4EFE6]">
                {issues.map((i, idx) => {
                  const S = SEV[i.severity] || SEV.warning;
                  return (
                    <div key={idx} className="p-3.5 flex items-start gap-3 text-sm" data-testid={`seo-issue-${idx}`}>
                      <span className={`mt-0.5 p-1.5 ${S.bg}`}><S.icon size={14} className={S.cls} /></span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{i.check}</p>
                        <p className="text-xs text-[#6E6D6A] mt-0.5">{i.message}</p>
                      </div>
                      {i.edit_url && <a href={i.edit_url} className="text-xs text-[#C5A059] underline shrink-0">Fix →</a>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )
      )}

      {tab === "feed" && (
        <div className="space-y-4" data-testid="merchant-feed-panel">
          <div className="bg-white border border-[#E5DEC9] p-5">
            <div className="flex items-center gap-2 mb-2"><Rss size={16} className="text-[#C5A059]" /><p className="font-semibold">Google Merchant Center Feed</p></div>
            <p className="text-sm text-[#6E6D6A] mb-4">Auto-generated from your live catalog (all active products & variants). Submit the XML URL in Google Merchant Center → Products → Feeds → "Scheduled fetch" for daily auto-sync.</p>
            {[
              ["XML Feed (recommended)", `${API}/feeds/google.xml`],
              ["JSON Feed (debugging)", `${API}/feeds/google.json`],
            ].map(([label, url]) => (
              <div key={url} className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold w-40 shrink-0">{label}</span>
                <code className="flex-1 text-xs bg-[#F4EFE6] px-3 py-2 truncate" data-testid={`feed-url-${label.includes("XML") ? "xml" : "json"}`}>{url}</code>
                <button onClick={() => copy(url, "Feed URL")} data-testid={`feed-copy-${label.includes("XML") ? "xml" : "json"}`} className="tq-btn-outline !py-2 !px-3" aria-label={`Copy ${label}`}><Copy size={13} /></button>
              </div>
            ))}
            <a href={`${API}/feeds/google.xml`} target="_blank" rel="noreferrer" data-testid="feed-preview-link" className="text-xs text-[#C5A059] underline mt-2 inline-block">Preview feed in new tab ↗</a>
          </div>
          <div className="bg-white border border-[#E5DEC9] p-5 text-sm text-[#6E6D6A] space-y-2">
            <p className="font-semibold text-[#121212]">Setup checklist</p>
            <ol className="list-decimal list-inside space-y-1 text-xs leading-relaxed">
              <li>Create / sign in to Google Merchant Center and verify your website URL (use the verification tag in Marketing settings).</li>
              <li>Products → Feeds → Add feed → Scheduled fetch → paste the XML URL above → daily frequency.</li>
              <li>Feed attributes (price, availability, GTIN/barcode, size, color) update automatically whenever you edit products.</li>
              <li>Enable "Free listings" and link Google Ads for Shopping campaigns.</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
